import React from "react";
import { Badge } from "../Badge";
import { Button } from "../Button";
import { getReasonLabel } from "../TipTapEditor/utils/suggestionLabels";
import { Body, Heading } from "../Typography";
import "./SuggestionPopoverContent.css";
import { Icon } from "../Icons";
import {
  parsePreviouslyMentionedSource,
  splitEarlierPassage,
} from "./utils/parsePreviousInformation";
import { parseSegmentLinks } from "./utils/parseSegmentLinks";

export type SuggestionPopoverVariant = "default" | "minimal";

export interface SuggestionPopoverContentProps {
  /**
   * Layout variant:
   * - "default": Vertical layout with full details (reason, explanation, confidence)
   * - "minimal": Horizontal compact layout with just reason and actions
   */
  variant?: SuggestionPopoverVariant;

  /**
   * Badge label displayed at the top (e.g., "Suggested Removal", "Suggested Delete")
   */
  badgeLabel: string;

  /**
   * Reason code for the suggestion (will be converted to human-readable label)
   */
  reason: string;

  /**
   * Detailed explanation for the suggestion
   */
  reasonDetails?: string | null;

  /**
   * AI confidence score (0-1)
   */
  confidence?: number | null;

  /**
   * Whether the suggestion is still valid (content hasn't changed).
   * When false, the Accept button is disabled.
   * Defaults to true.
   */
  isValid?: boolean;

  /**
   * Callback when suggestion is accepted
   */
  onAccept: () => void;

  /**
   * Callback when suggestion is rejected/ignored
   */
  onReject: () => void;

  /**
   * Callback when "View source entry" link is clicked.
   * When provided, renders a navigation link in the popover.
   */
  onViewRelatedSegment?: () => void;

  /**
   * Callback to navigate to a segment by its logical segment ID.
   * When provided, UUIDs in reasonDetails are rendered as clickable links.
   */
  onNavigateToSegment?: (segmentId: string) => void;
}

/**
 * Renders text with UUID segments as clickable links.
 * Falls back to plain text when onNavigateToSegment is not provided.
 */
const renderTextWithSegmentLinks = (
  text: string,
  onNavigateToSegment?: (segmentId: string) => void,
): React.ReactNode => {
  if (!onNavigateToSegment) return text;

  const parts = parseSegmentLinks(text);
  if (parts.length === 1 && parts[0].type === "text") return text;

  return parts.map((part, i) =>
    part.type === "uuid" ? (
      <Button
        key={i}
        className="p-0"
        variant="link"
        size="sm"
        aria-label="Navigate to related segment"
        onClick={(e) => {
          e.stopPropagation();
          onNavigateToSegment(part.value);
        }}
      >
        {part.value.slice(0, 8)}
      </Button>
    ) : (
      part.value
    ),
  );
};

/**
 * Shared content component for suggestion popovers.
 *
 * Used by both SuggestionBubbleMenu (TipTap editor) and SuggestionPopover.
 * Provides consistent UI for displaying AI suggestions with Accept/Reject actions.
 */
export const SuggestionPopoverContent: React.FC<
  SuggestionPopoverContentProps
> = ({
  variant = "default",
  badgeLabel,
  reason,
  reasonDetails,
  confidence,
  isValid = true,
  onAccept,
  onReject,
  onViewRelatedSegment,
  onNavigateToSegment,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)
      return;
    const key = event.key.toLowerCase();
    if (key !== "k" && key !== "d") return;
    event.preventDefault();
    event.stopPropagation();
    if (event.repeat || event.nativeEvent.isComposing) return;
    if (key === "k") onReject();
    else if (isValid) onAccept();
  };

  if (variant === "minimal") {
    return (
      <div
        className="indigo-suggestion-popover-content--minimal"
        onKeyDown={handleKeyDown}
      >
        <Badge label={badgeLabel} size="lg" />
        <div className="indigo-suggestion-popover-content__reason">
          <Body size="md">{getReasonLabel(reason)}</Body>
        </div>
        <div className="indigo-suggestion-popover-content__actions">
          <Button
            variant="ghost"
            size="md"
            onClick={onReject}
            title="Keep content (K when suggestion actions are focused)"
            aria-label="Keep content"
            aria-keyshortcuts="k"
            data-suggestion-keep
          >
            <span className="indigo-suggestion-action-label">
              <kbd className="indigo-suggestion-shortcut">K</kbd>eep
            </span>
          </Button>
          <Button
            variant="ghost"
            size="md"
            onClick={onAccept}
            disabled={!isValid}
            title={
              isValid
                ? "Accept suggestion (delete text)"
                : "Cannot accept - content changed"
            }
            aria-label="Delete suggested content"
            aria-keyshortcuts="d"
          >
            <span className="indigo-suggestion-action-label">
              <kbd className="indigo-suggestion-shortcut">D</kbd>elete
            </span>
          </Button>
        </div>
      </div>
    );
  }

  const { explanation, earlierPassages } =
    reason === "DUPLICATE_IN_ANOTHER_SEGMENT"
      ? splitEarlierPassage(reasonDetails ?? "")
      : { explanation: reasonDetails, earlierPassages: [] };
  const sourceEntry =
    reason === "DUPLICATE_IN_ANOTHER_SEGMENT" && onViewRelatedSegment
      ? parsePreviouslyMentionedSource(explanation ?? "")
      : null;

  // Default variant - detailed vertical layout
  return (
    <div
      className="indigo-suggestion-popover-content--default"
      onKeyDown={handleKeyDown}
    >
      <div className="indigo-suggestion-popover-content__header">
        <Badge label={badgeLabel} size="sm" />
        <Heading level={3}>{getReasonLabel(reason)}</Heading>
      </div>

      {explanation && (
        <Body size="md" className="font-normal">
          {sourceEntry ? (
            <>
              {sourceEntry.prefix}
              <Button
                className="indigo-suggestion-popover-content__source-link"
                variant="link"
                size="md"
                aria-label={`View source entry: ${sourceEntry.entryLabel}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewRelatedSegment?.();
                }}
              >
                {sourceEntry.entryLabel}
              </Button>
              {renderTextWithSegmentLinks(
                sourceEntry.suffix,
                onNavigateToSegment,
              )}
            </>
          ) : (
            renderTextWithSegmentLinks(explanation, onNavigateToSegment)
          )}
        </Body>
      )}

      {earlierPassages.some(Boolean) && (
        <div className="indigo-suggestion-popover-content__passage">
          <Body size="sm" className="text-indigo-text-secondary">
            {earlierPassages.length === 1
              ? "Earlier passage"
              : "Earlier passages and explanations"}
          </Body>
          {earlierPassages.map((passage, index) => (
            <Body
              key={`${index}-${passage}`}
              as={earlierPassages.length === 1 ? "blockquote" : "p"}
              size="md"
              className="indigo-suggestion-popover-content__quote"
            >
              {renderTextWithSegmentLinks(passage, onNavigateToSegment)}
            </Body>
          ))}
        </div>
      )}

      {onViewRelatedSegment && !sourceEntry && (
        <Button
          variant="link"
          size="sm"
          icon={<Icon name="link" />}
          onClick={(e) => {
            e.stopPropagation();
            onViewRelatedSegment();
          }}
        >
          View source entry
        </Button>
      )}

      <div className="indigo-suggestion-popover-content__footer">
        {confidence != null && (
          <Body size="sm" className="text-indigo-text-disabled">
            Confidence: {confidence}
          </Body>
        )}
        <div className="flex-1"></div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReject}
          title="Keep content (K when suggestion actions are focused)"
          aria-label="Keep content"
          aria-keyshortcuts="k"
          data-suggestion-keep
        >
          <span className="indigo-suggestion-action-label">
            <kbd className="indigo-suggestion-shortcut">K</kbd>eep
          </span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onAccept}
          disabled={!isValid}
          title={
            isValid
              ? "Accept suggestion (delete text)"
              : "Cannot accept - content changed"
          }
          aria-label="Delete suggested content"
          aria-keyshortcuts="d"
        >
          <span className="indigo-suggestion-action-label">
            <kbd className="indigo-suggestion-shortcut">D</kbd>elete
          </span>
        </Button>
      </div>
    </div>
  );
};
